import React, { useState, useEffect } from 'react';
import ReloadButton from '../LoadingButtons/ReloadButton';
import DataLoader from '../Spinner/DataLoader';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import EditTrainingModal from '../EditTraningModal/EditTraningModal';
import AddNewTrainingModal from '../AddNewTraningModal/AddNewTraningModal';
import CopyFooter from '../CopyFooter/CopyFooter';
import TrainingSections from '../TrainingSections/TrainingSections';

const commonStyles = `
.page-wrapper {
    padding: 20px 0 0 0;
    background: transparent;
    flex: 1;
    display: flex;
    flex-direction: column;
    font-family: 'Inter', 'Roboto', sans-serif;
    font-size: 12px;
    overflow: hidden;
    min-height: 0;
    position: relative;
}
  .toolbar {
    display: flex;
    width: 100%;
    background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    margin-bottom: 1px;
  }
  .toolbar-left { display: flex; align-items: center; }
  .toolbar-right { display: flex; align-items: center; gap: 4px; }

  .btn-refresh {
    display: flex; align-items: center; justify-content: center;
    background: var(--color-primary); color: #fff; border: none;
    border-radius: 6px; width: 32px; height: 32px; cursor: pointer;
    font-size: 13px; flex-shrink: 0;
  }
  .btn-refresh:hover { background: var(--color-primary-dark); }

  .toolbar-right .btn-primary,
  .toolbar-right .btn-danger,
  .toolbar-right .btn-success {
    display: inline-flex; align-items: center; justify-content: center;
    background: #2563eb; color: #fff; border: 1px solid #2563eb;
    border-radius: 6px; height: 32px; padding: 6px 12px; cursor: pointer;
    font-size: 12px; font-weight: 600;
    line-height: 1.2;
  }
  .toolbar-right .btn-primary:hover:not(:disabled),
  .toolbar-right .btn-danger:hover:not(:disabled),
  .toolbar-right .btn-success:hover:not(:disabled) {
    background: #1d4ed8;
    border-color: #1d4ed8;
  }
  .toolbar-right .btn-primary:disabled,
  .toolbar-right .btn-danger:disabled,
  .toolbar-right .btn-success:disabled {
    background: #bfdbfe;
    border-color: #bfdbfe;
    cursor: not-allowed;
  }

  .table-wrapper {
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .table-scroll {
    overflow-x: auto;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    color: #374151;
  }
  .data-table thead tr {
    background: #f3f4f6;
    position: sticky;
    top: 0;
    z-index: 5;
  }
  .data-table th {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    color: #374151;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
  }
  .data-table tbody tr {
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    transition: background 0.15s;
    height: 35px;
  }
  .data-table tbody tr:hover { background: #f3f4f6; }
  .data-table tbody tr.row-selected { background: #eff6ff !important; }
  .data-table td {
    padding: 6px 12px;
    color: #374151;
    white-space: nowrap;
  }
  .table-footer {
    background: #fff;
    border-top: 1px solid #e5e7eb;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 11px;
    color: #6b7280;
  }
  .page-divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner-wrap {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    flex: 1; gap: 12px; color: #9ca3af;
  }
  .spinner-circle {
    width: 32px; height: 32px; border-radius: 50%;
    border: 3px solid #e5e7eb;
    border-top-color: var(--color-primary);
    animation: spin 0.8s linear infinite;
  }
`;

const Trainings = () => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowName, setSelectedRowName] = useState("");
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedText, setCopiedText] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showSectionsModal, setShowSectionsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [response] = await Promise.all([
        apiFetch(BASE_URL + '/trainings'),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      const data = await response.json();
      setTrainings(data.trainings || []);
      setSelectedRowId(null);
      setSelectedRowName("");
    } catch (err) {
      console.error("Məlumat alınarkən xəta:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (item) => {
    setSelectedRowId(item.id);
    setSelectedRowName(item.name);
  };

  const handleRowDoubleClick = (item) => {
    setSelectedRowId(item.id);
    setSelectedRowName(item.name);
    setShowSectionsModal(true);
  };

  const handleCellClick = (text) => {
    setCopiedText(text?.toString() || "");
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await apiFetch(`${BASE_URL}/trainings/${selectedRowId}`, {
        method: 'DELETE',
      });
      if (response.ok) { setShowModal(false); fetchData(); }
    } catch (err) {
      console.error("Serverlə əlaqə xətası:", err);
    }
  };

  return (
    <div className="page-wrapper">
      <style>{commonStyles}</style>

      <div className="toolbar">
        <div className="toolbar-left">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <svg viewBox="0 0 20 20" fill="none" style={{ position: 'absolute', left: 7, width: 13, height: 13, color: '#9ca3af', pointerEvents: 'none' }}>
              <circle cx="8.5" cy="8.5" r="5" stroke="#9ca3af" strokeWidth="1.6"/>
              <path d="M13 13l3 3" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Təlim axtar..."
              style={{ paddingLeft: 26, paddingRight: searchQuery ? 24 : 8, height: 28, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none', width: 200, background: '#fff', color: '#374151' }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                <svg viewBox="0 0 20 20" fill="none" style={{ width: 12, height: 12 }}>
                  <path d="M5 5l10 10M15 5L5 15" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="toolbar-right">
          <ReloadButton onClick={fetchData} loading={loading} />
          <button className="btn-primary" disabled={!selectedRowId} onClick={() => setShowEditModal(true)}>
            Redaktə et
          </button>
          <button className="btn-danger" disabled={!selectedRowId} onClick={() => setShowModal(true)}>
            Sil
          </button>
          <button className="btn-success" onClick={() => setShowAddModal(true)}>
            Yeni təlim
          </button>
        </div>
      </div>

      <hr className="page-divider" />

      <DataLoader show={loading} />
      {!loading && (
        <div className="table-wrapper">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Təlim Adı</th>
                  <th>Təsvir</th>
                  <th>Tarix</th>
                </tr>
              </thead>
              <tbody>
                {trainings.filter(item =>
                  !searchQuery ||
                  item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.description?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((item) => (
                  <tr
                    key={item.id}
                    className={selectedRowId === item.id ? 'row-selected' : ''}
                    onClick={() => handleRowClick(item)}
                    onDoubleClick={() => handleRowDoubleClick(item)}
                  >
                    <td onClick={() => handleCellClick(item.id)}>{item.id}</td>
                    <td style={{fontWeight: 600}} onClick={() => handleCellClick(item.name)}>{item.name}</td>
                    <td onClick={() => handleCellClick(item.description)}>{item.description || "—"}</td>
                    <td onClick={() => handleCellClick(item.register)}>{item.register}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CopyFooter value={copiedText} />
        </div>
      )}

      <ConfirmModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirmDelete}
        title="Silməni Təsdiqlə"
        message={<><strong>{selectedRowName}</strong> adlı təlimi silmək istədiyinizə əminsiniz?</>}
        confirmText="Bəli, Sil"
      />

      <AddNewTrainingModal show={showAddModal} onHide={() => setShowAddModal(false)} onSuccess={fetchData} />
      <EditTrainingModal show={showEditModal} onHide={() => setShowEditModal(false)} trainingId={selectedRowId} onSuccess={fetchData} />
      <TrainingSections show={showSectionsModal} onHide={() => setShowSectionsModal(false)} trainingId={selectedRowId} trainingName={selectedRowName} />
    </div>
  );
};

export default Trainings;
